import React, { useEffect, useState } from 'react';
import { 
    Container, 
    TextField, 
    Button, 
    Typography, 
    Box, 
    Card, 
    CardContent, 
    styled, 
    alpha, 
    useTheme, 
    useMediaQuery 
} from '@mui/material';
import { Login as LoginIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';

// Custom theme colors (using the same brand palette as OnBoarding)
const brandColors = {
    primary: {
        main: '#3B82F6',
        light: '#60A5FA',
        dark: '#1E40AF',
    },
    secondary: {
        main: '#8B5CF6',
        light: '#A78BFA',
        dark: '#6D28D9',
    },
    accent: {
        main: '#F97316',
        light: '#FB923C',
        dark: '#EA580C',
    }
};

// Styled Components
const LoginContainer = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    background: `linear-gradient(135deg, 
    #030712 0%, 
    ${alpha(brandColors.primary.dark, 0.1)} 25%,
    ${alpha(brandColors.secondary.dark, 0.05)} 75%,
    #030712 100%)`,
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const LoginCard = styled(Card)(({ theme }) => ({
    background: `linear-gradient(135deg, 
    ${alpha('#1E293B', 0.8)} 0%, 
    ${alpha(brandColors.primary.main, 0.05)} 100%)`,
    backdropFilter: 'blur(20px)',
    border: `1px solid ${alpha(brandColors.primary.main, 0.2)}`,
    borderRadius: '24px',
    boxShadow: `0 20px 40px ${alpha(brandColors.primary.main, 0.1)}`,
    maxWidth: 450,
    width: '100%',
}));

const BrandButton = styled(Button)(({ theme }) => ({
    background: `linear-gradient(135deg, ${brandColors.primary.main}, ${brandColors.secondary.main})`,
    color: 'white',
    fontWeight: 600,
    padding: '12px 32px',
    borderRadius: '50px',
    textTransform: 'none',
    fontSize: '1.1rem',
    gap: '0 16px',
    '&:hover': {
        background: `linear-gradient(135deg, ${brandColors.primary.light}, ${brandColors.secondary.light})`,
        transform: 'translateY(-2px)',
        boxShadow: `0 10px 30px ${alpha(brandColors.primary.main, 0.4)}`,
    },
    '&:disabled': {
        background: alpha('#1E293B', 0.6),
        color: '#64748B',
    },
    transition: 'all 0.3s ease',
    '& .MuiButton-endIcon, & .MuiButton-startIcon': {
        marginInlineStart: '12px',
        marginInlineEnd: '0px',
    },
    '[dir="rtl"] & .MuiButton-endIcon, [dir="rtl"] & .MuiButton-startIcon': {
        marginInlineStart: '0px',
        marginInlineEnd: '12px',
    },
}));

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  console.log(isMobile);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login(username, password);
    } catch (err) {
      setError(t('login.error'));
    }
  };

  useEffect(() => {
    if (user) {
      if (user.is_admin) {
        navigate('/users');
      } else {
        navigate('/');
      }
    }
  }, [user, navigate]);

  return (
    <LoginContainer>
      {/* Animated Background Elements */}
      <Box sx={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        {Array.from({ length: 20 }).map((_, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `twinkle ${2 + Math.random() * 3}s infinite`,
              animationDelay: `${Math.random() * 3}s`,
              '@keyframes twinkle': {
                '0%, 100%': { opacity: 0.3 },
                '50%': { opacity: 0.8 },
              },
            }}
          >
            <Box 
              sx={{ 
                width: 4, 
                height: 4, 
                borderRadius: '50%', 
                backgroundColor: 'white',
                boxShadow: '0 0 10px rgba(255, 255, 255, 0.5)'
              }} 
            />
          </Box>
        ))}
      </Box>

      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <LoginCard>
          <CardContent sx={{ p: { xs: 4, md: 6 } }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box sx={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${brandColors.primary.main}, ${brandColors.secondary.main})`,
                mb: 3,
                boxShadow: `0 10px 30px ${alpha(brandColors.primary.main, 0.3)}`,
              }}>
                <LoginIcon sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                {t('login.title')}
              </Typography>
              <Typography variant="body1" sx={{ color: '#CBD5E1' }}>
                {t('login.subtitle') || 'Welcome back to your astrological journey'}
              </Typography>
            </Box>

            {/* Login Form */}
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
              <TextField
                fullWidth
                label={t('login.username')}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                margin="normal"
                required
                inputProps={{
                  style: {
                    textAlign: isRTL ? 'right' : 'left'
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: alpha('#1E293B', 0.6),
                    '& fieldset': { borderColor: alpha(brandColors.primary.main, 0.3) },
                    '&:hover fieldset': { borderColor: brandColors.primary.main },
                    '&.Mui-focused fieldset': { borderColor: brandColors.primary.light },
                  },
                  '& .MuiInputLabel-root': { color: '#CBD5E1' },
                  '& .MuiInputBase-input': { color: 'white' },
                }}
              />
              
              <TextField
                fullWidth
                label={t('login.password')}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                required
                inputProps={{
                  style: {
                    textAlign: isRTL ? 'right' : 'left'
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <Button
                      onClick={() => setShowPassword(!showPassword)}
                      sx={{ 
                        minWidth: 'auto', 
                        color: '#CBD5E1',
                        '&:hover': { backgroundColor: alpha(brandColors.primary.main, 0.1) }
                      }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </Button>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: alpha('#1E293B', 0.6),
                    '& fieldset': { borderColor: alpha(brandColors.primary.main, 0.3) },
                    '&:hover fieldset': { borderColor: brandColors.primary.main },
                    '&.Mui-focused fieldset': { borderColor: brandColors.primary.light },
                  },
                  '& .MuiInputLabel-root': { color: '#CBD5E1' },
                  '& .MuiInputBase-input': { color: 'white' },
                }}
              />

              {error && (
                <Typography 
                  color="error" 
                  sx={{ 
                    mt: 2, 
                    p: 2, 
                    backgroundColor: alpha('#EF4444', 0.1),
                    borderRadius: 2,
                    border: `1px solid ${alpha('#EF4444', 0.3)}`,
                    color: '#FCA5A5'
                  }}
                >
                  {error}
                </Typography>
              )}

              <BrandButton
                type="submit"
                fullWidth
                size="large"
                startIcon={<LoginIcon />}
                sx={{ mt: 4, mb: 2 }}
              >
                {t('login.submit')}
              </BrandButton>
            </Box>

            {/* Footer */}
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#64748B' }}>
                {t('login.dataSecure') || 'Your data is secure and encrypted'}
              </Typography>
            </Box>
          </CardContent>
        </LoginCard>
      </Container>
    </LoginContainer>
  );
};

export default Login; 